import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { cn } from '@/lib/utils';
import {
  blockExcelFunction,
  blockSettings,
  isReportState,
  isSelectedBlockState,
  useScopedSetRecoilState,
  viewImageId,
  viewRichText,
} from 'venn-state';
import { EditorContext, UserContext } from '../../../contexts';
import RichTextEditor from '../../../richtext-editor/RichTextEditor';
import type { EditorValue } from '../../../richtext-editor';
import { createReactEditor } from '../../../richtext-editor';
import { useAppPrintMode } from '../../../print/AppPrintMode';
import { isNil } from 'lodash';
import { assertExhaustive } from 'venn-utils';

type TextFormatBlockType = 'IMAGE' | 'PAGE_BREAK' | 'MARKDOWN';

function isTextFormatBlockType(type: string): type is TextFormatBlockType {
  return type === 'IMAGE' || type === 'PAGE_BREAK' || type === 'MARKDOWN';
}

interface TextFormatContentProps {
  id: string;
}

export const TextFormatContent = ({ id }: TextFormatContentProps) => {
  const isBlockSelected = useRecoilValue(isSelectedBlockState(id));
  const blockSetting = useRecoilValue(blockSettings(id));
  const isReport = useRecoilValue(isReportState);
  const customBlockType = blockSetting.customBlockType;
  const setExportFunction = useScopedSetRecoilState(blockExcelFunction(id));

  useEffect(() => {
    setExportFunction({ excelFunction: undefined });
  }, [setExportFunction]);

  if (isTextFormatBlockType(customBlockType)) {
    switch (customBlockType) {
      case 'IMAGE':
        return <Image id={id} isReport={isReport} />;
      case 'PAGE_BREAK':
        return <PageBreak active={isBlockSelected} />;
      case 'MARKDOWN':
        return <Text id={id} active={isBlockSelected} isReport={isReport} />;
      default:
        return assertExhaustive(customBlockType);
    }
  } else {
    return null;
  }
};

const Image = ({ id, isReport }: { id: string; isReport?: boolean }) => {
  const imageId = useRecoilValue(viewImageId(id));
  const noImage = isNil(imageId);

  return (
    <div
      data-testid="image-block"
      className={cn('flex flex-col justify-center items-center p-[5px] h-full w-full', noImage && 'min-h-[100px]')}
    >
      {noImage ? (
        <div>
          <i className="fa-solid fa-image pr-[8px]" />
          Please Select or Upload an Image
        </div>
      ) : (
        <img
          src={`/api/images/${imageId}`}
          alt={imageId}
          className={cn('inline-block', isReport ? 'object-contain w-full h-full' : 'object-scale-down')}
        />
      )}
    </div>
  );
};

export const PageBreak = ({ active }: { active?: boolean }) => {
  const { inPrintMode } = useAppPrintMode();

  if (inPrintMode) {
    return null;
  }

  return (
    <div data-testid="page-break">
      <div className="flex items-center mb-[20px] py-[10px] print:hidden">
        <div className="w-full flex items-center">
          <div className="border border-dashed border-venn-grey-40 h-px w-full" />
        </div>
        <div className="mx-[20px]">
          <p
            className={cn(
              'm-0 cursor-default uppercase h-[22px] px-[15px] text-[11px] whitespace-nowrap text-venn-grey-50 font-bold leading-[22px] transition-colors duration-200',
              !active ? 'hover:text-venn-grey-80' : 'text-venn-grey-80',
            )}
          >
            Page Break
          </p>
        </div>
        <div className="w-full flex items-center">
          <div className="border border-dashed border-venn-grey-40 h-px w-full" />
        </div>
      </div>
    </div>
  );
};

const Text = ({ id, active, isReport }: { id: string; active: boolean; isReport?: boolean }) => {
  const { inPrintModeOrReportLab } = useAppPrintMode();
  const { hasPermission } = useContext(UserContext);
  const { setActiveEditor, recalculateTextControlsState } = useContext(EditorContext);
  const editor = useMemo(() => createReactEditor(), []);
  const isReadOnly = !hasPermission('STUDIO_EDIT_TEXT');

  const [value, setValue] = useRecoilState(viewRichText(id));

  const handleOnChange = useCallback(
    (value: EditorValue) => {
      setValue(value);
      recalculateTextControlsState();
    },
    [setValue, recalculateTextControlsState],
  );

  useEffect(() => {
    if (active) {
      setActiveEditor(editor);
    }
  }, [active, editor, setActiveEditor]);

  const previewStyle = inPrintModeOrReportLab;

  return (
    <div
      className={cn(
        previewStyle ? 'p-0' : 'px-[20px]',
        'cursor-pointer',
        isReport && '[&_p]:text-[14px] [&_ul]:text-[14px]',
      )}
    >
      {editor && (
        <RichTextEditor
          controlledEditor={editor}
          initialValue={value}
          handleOnChange={handleOnChange}
          readOnly={isReadOnly}
        />
      )}
    </div>
  );
};
